import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useMedia } from '@pkg/hooks';

import { DEFAULT_CONTEXT } from './default-context';
import { mediaQueries } from './media-queries';
import { type MediaQueryContextDescriptor, type MediaQueryScope } from './types';

export interface MediaQueryProviderProps {
    children: ReactNode;
}

export const MediaQueryContext = createContext(DEFAULT_CONTEXT);

export function MediaQueryProvider({ children }: MediaQueryProviderProps) {
    const phablet = useMedia(mediaQueries.phablet);
    const tablet = useMedia(mediaQueries.tablet);
    const desktop = useMedia(mediaQueries.desktop);
    const widescreen = useMedia(mediaQueries.widescreen);
    const quadHdY = useMedia(mediaQueries.quadHdY);
    const ultrawide = useMedia(mediaQueries.ultrawide);

    // TODO: This will initialize as `true`,
    // which may not be desired.
    const mobile = !phablet;

    const landscape = useMedia(mediaQueries.landscape);
    const prefersReducedMotion = useMedia(mediaQueries.prefersReducedMotion);
    const usingMouse = useMedia(mediaQueries.usingMouse);

    const value: MediaQueryContextDescriptor = useMemo(() => {
        const queries: MediaQueryScope = {
            mobile,
            phablet,
            tablet,
            desktop,
            widescreen,
            quadHdY,
            ultrawide,
            landscape,
            prefersReducedMotion,
            usingMouse,
        };

        return {
            ...queries,
            betweenBreakpoints(min, max) {
                const minWidth = min === 'mobile' || queries[min];
                // TODO: Should we check if `min` is
                // a later breakpoint than `max` and throw and error?
                return min === max ? queries[min] : minWidth && !queries[max];
            },
        };
    }, [mobile, phablet, tablet, desktop, widescreen, quadHdY, ultrawide, landscape, prefersReducedMotion, usingMouse]);

    return <MediaQueryContext.Provider value={value}>{children}</MediaQueryContext.Provider>;
}

export function useMediaQuery() {
    return useContext(MediaQueryContext);
}
